import {z} from 'zod';
import {toast} from 'react-toastify';
import React, {FC, useMemo, useState} from 'react';
import {zodResolver} from '@hookform/resolvers/zod';
import {VerdocsSpinner} from '@verdocs/web-sdk-react';
import {Controller, FormProvider, useForm, useWatch} from 'react-hook-form';
import {ISetWebhookRequest, IWebhook, TWebhookAuthMethod, TWebhookEvent} from '@verdocs/js-sdk';
import {VerdocsEndpoint, WEBHOOK_EVENTS, rotateWebhookSecret, setWebhooks} from '@verdocs/js-sdk';
import {Button, Checkbox, FormHint, FormLabel, FormRow, SegmentedControl, Switch, TextInput} from '../Components';
import {track} from '../Managers/Analytics';
import {Dialog} from './Dialog';

const GROUPED_EVENTS: {label: string; events: TWebhookEvent[]}[] = [
  {label: 'Envelopes', events: ['envelope_created', 'envelope_updated', 'envelope_completed', 'envelope_canceled', 'envelope_expired']},
  {
    label: 'Recipients',
    events: [
      'recipient_invited',
      'recipient_opened',
      'recipient_submitted',
      'recipient_updated',
      'recipient_declined',
      'recipient_delegated',
      'recipient_reminded',
      'recipient_auth_fail',
      'recipient_disclosure_accepted',
      'recipient_docs_downloaded',
      'recipient_invite_failed',
    ],
  },
  {label: 'Templates', events: ['template_created', 'template_updated', 'template_deleted', 'template_used']},
  {label: 'Other', events: ['kba_event', 'entitlement_used', 'organization_deleted']},
];

// New SDK events fall into Other
const LISTED = new Set<string>(GROUPED_EVENTS.flatMap((group) => group.events));
const EVENT_GROUPS = GROUPED_EVENTS.map((group) => ({
  label: group.label,
  events: [...group.events, ...(group.label === 'Other' ? WEBHOOK_EVENTS.filter((event) => !LISTED.has(event)) : [])],
}));

/** The API masks stored secrets as "..." plus the last four characters. */
const lastFour = (masked?: string | null) => (masked || '').replace(/^\.\.\./, '');

const buildSchema = (hasClientSecret: boolean) =>
  z
    .object({
      url: z.url({protocol: /^https$/, hostname: z.regexes.domain, message: 'Please enter a valid HTTPS URL.'}).trim(),
      active: z.boolean(),
      auth_method: z.enum(['none', 'hmac', 'client_credentials']),
      token_endpoint: z.string().trim(),
      client_id: z.string().trim(),
      client_secret: z.string(),
      scope: z.string().trim(),
      events: z.record(z.string(), z.boolean()),
    })
    .superRefine((data, ctx) => {
      if (data.auth_method !== 'client_credentials') {
        return;
      }

      if (!data.token_endpoint) {
        ctx.addIssue({code: 'custom', message: 'Token endpoint is required.', path: ['token_endpoint']});
      }

      if (!data.client_id) {
        ctx.addIssue({code: 'custom', message: 'Client ID is required.', path: ['client_id']});
      }

      if (!data.client_secret && !hasClientSecret) {
        ctx.addIssue({code: 'custom', message: 'Client secret is required.', path: ['client_secret']});
      }
    });

interface FormValues {
  url: string;
  active: boolean;
  auth_method: TWebhookAuthMethod;
  token_endpoint: string;
  client_id: string;
  client_secret: string;
  scope: string;
  events: Record<string, boolean>;
}

interface EditWebhookDialogProps {
  /** The saved webhook, or null when the organization doesn't have one yet. */
  webhook: IWebhook | null;
  rotating: boolean;
  onRotateSecret: () => void;
  /** Called after a successful save, with the signing secret if one was generated. */
  onSaved: (generatedSecret: string | null) => void;
  onClose: () => void;
}

export const EditWebhookDialog: FC<EditWebhookDialogProps> = ({webhook, rotating, onRotateSecret, onSaved, onClose}) => {
  const [submitting, setSubmitting] = useState(false);
  const hasClientSecret = !!webhook?.client_secret;
  const schema = useMemo(() => buildSchema(hasClientSecret), [hasClientSecret]);

  const form = useForm<FormValues>({
    mode: 'all',
    resolver: zodResolver(schema),
    defaultValues: {
      url: webhook?.url || '',
      active: webhook?.active ?? true,
      auth_method: webhook?.auth_method || 'hmac',
      token_endpoint: webhook?.token_endpoint || '',
      client_id: webhook?.client_id || '',
      client_secret: '',
      scope: webhook?.scope || '',
      events: Object.fromEntries(WEBHOOK_EVENTS.map((event) => [event, !!webhook?.events?.[event]])),
    },
  });

  const authMethod = useWatch({control: form.control, name: 'auth_method'});
  const events = useWatch({control: form.control, name: 'events'});
  const selectedCount = WEBHOOK_EVENTS.filter((event) => events[event]).length;

  const setEvents = (changed: string[], checked: boolean) => {
    const next = {...form.getValues('events')};
    changed.forEach((event) => (next[event] = checked));
    form.setValue('events', next, {shouldDirty: true});
  };

  const onSubmit = async (values: FormValues) => {
    // Disabled Buttons can still submit forms
    if (submitting || (webhook && !form.formState.isDirty)) {
      return;
    }

    const params: ISetWebhookRequest = {
      url: values.url,
      active: values.active,
      auth_method: values.auth_method,
      events: Object.fromEntries(WEBHOOK_EVENTS.map((event) => [event, !!values.events[event]])) as Record<TWebhookEvent, boolean>,
      token_endpoint: values.token_endpoint || null,
      client_id: values.client_id || null,
      scope: values.scope || null,
    };

    // Only send a client secret the user typed
    if (values.client_secret) {
      params.client_secret = values.client_secret;
    }

    setSubmitting(true);
    try {
      const saved = await setWebhooks(VerdocsEndpoint.getDefault(), params);
      track('settings_updated', {section: 'webhooks'});
      toast.success('Webhook saved.');

      // HMAC needs a secret, so generate one on first save
      let generatedSecret: string | null = null;
      if (values.auth_method === 'hmac' && !webhook?.secret_key && !saved?.secret_key) {
        try {
          const rotated = await rotateWebhookSecret(VerdocsEndpoint.getDefault());
          generatedSecret = rotated.secret_key || null;
        } catch (e: any) {
          console.log('Error generating webhook secret', e);
          toast.error(`Unable to generate a signing secret: ${e.response?.data?.error || e.message}`);
        }
      }

      onSaved(generatedSecret);
    } catch (e: any) {
      console.log('Error saving webhook', e);
      toast.error(`Unable to save the webhook: ${e.response?.data?.error || e.message}`);
      setSubmitting(false);
    }
  };

  return (
    <Dialog size="wide" onCancel={onClose}>
      <div className="text-lg font-medium text-[#33364b]">{webhook ? 'Edit endpoint' : 'Add an endpoint'}</div>
      <div className="mb-4 text-[13px] leading-normal text-gray-500">
        We POST a JSON payload to this URL each time one of the selected events happens.
      </div>

      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex min-h-0 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto pr-1">
            <TextInput
              required
              name="url"
              label="Endpoint URL"
              placeholder="https://api.example.com/hooks/verdocs"
              hint="Must be HTTPS and reachable from the public internet."
            />

            <FormRow>
              <FormLabel>Status</FormLabel>
              <Controller
                name="active"
                control={form.control}
                render={({field}) => (
                  <label className="flex cursor-pointer items-start gap-2.5">
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      className="data-[state=checked]:bg-[#654dcb] data-[state=unchecked]:bg-gray-200"
                    />
                    <span>
                      <span className="block text-[13px] text-[#33364b]">Active</span>
                      <span className="block text-[12px] text-gray-500">Pausing keeps the configuration but stops delivery.</span>
                    </span>
                  </label>
                )}
              />
            </FormRow>

            <FormRow>
              <FormLabel>Authentication</FormLabel>
              <div>
                <SegmentedControl
                  value={authMethod}
                  onChange={(key) => form.setValue('auth_method', key as TWebhookAuthMethod, {shouldDirty: true, shouldValidate: true})}
                  options={[
                    {key: 'none', label: 'None'},
                    {key: 'hmac', label: 'HMAC'},
                    {key: 'client_credentials', label: 'OAuth'},
                  ]}
                />
              </div>
              <FormHint>How your endpoint can prove the request came from us.</FormHint>
            </FormRow>

            {authMethod === 'hmac' && (
              <div className="mb-4 rounded-lg border border-[#ebecf0] bg-[#fbfbfc] p-4">
                <div className="mb-2 text-[13px] font-medium text-[#33364b]">Signing secret</div>
                {webhook?.secret_key && (
                  <div className="mb-2 truncate rounded-md border border-[#dcdee5] bg-[#f7f7f9] px-3 py-2 font-mono text-[12px] text-[#33364b]">
                    {'•'.repeat(24)}
                    {lastFour(webhook.secret_key)}
                  </div>
                )}
                <div className="text-[12px] leading-normal text-gray-500">
                  We send the hex HMAC-SHA256 of the payload's <span className="whitespace-nowrap font-mono">data</span> field, keyed with
                  the signing secret, in the <span className="whitespace-nowrap font-mono">x-webhook-signature</span> header.
                </div>
                {webhook && (
                  <Button
                    type="button"
                    size="xsmall"
                    variant="secondary"
                    className="mt-3"
                    disabled={rotating}
                    label={webhook.secret_key ? 'Reset secret' : 'Generate secret'}
                    onClick={onRotateSecret}
                  />
                )}
              </div>
            )}

            {authMethod === 'client_credentials' && (
              <div className="mb-4 rounded-lg border border-[#ebecf0] bg-[#fbfbfc] p-4 pb-0">
                <TextInput required name="token_endpoint" label="Token endpoint" placeholder="https://auth.example.com/oauth2/token" />
                <TextInput required name="client_id" label="Client ID" placeholder="verdocs-webhooks" />
                <TextInput
                  required={!hasClientSecret}
                  type="password"
                  name="client_secret"
                  label="Client secret"
                  placeholder={hasClientSecret ? `••••••••${lastFour(webhook?.client_secret)}` : '••••••••'}
                  hint={hasClientSecret ? 'Leave blank to keep the current secret.' : undefined}
                />
                <TextInput name="scope" label="Scope" placeholder="webhooks:write" />
              </div>
            )}

            <div className="mb-1 flex items-center justify-between">
              <div className="text-[13px] font-medium text-[#33364b]">Events</div>
              <div className="text-[12px] text-gray-500">
                {selectedCount} of {WEBHOOK_EVENTS.length} selected
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 tablet:grid-cols-2">
              {EVENT_GROUPS.map((group) => {
                const allOn = group.events.every((event) => events[event]);

                return (
                  <div key={group.label} className="rounded-lg border border-[#ebecf0] p-3">
                    <button
                      type="button"
                      title={allOn ? 'Clear all' : 'Select all'}
                      onClick={() => setEvents(group.events, !allOn)}
                      className="mb-1.5 text-[12px] font-medium uppercase tracking-wide text-gray-400 hover:text-[#654dcb]">
                      {group.label}
                    </button>
                    {group.events.map((event) => (
                      <Checkbox key={event} label={event} checked={!!events[event]} onChange={(checked) => setEvents([event], checked)} />
                    ))}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-4 flex flex-row items-center justify-end gap-2 border-t border-[#f2f3f5] pt-4">
            {submitting && <VerdocsSpinner mode="dark" />}
            <Button type="button" size="small" variant="secondary" label="Cancel" onClick={onClose} disabled={submitting} />
            <Button
              type="submit"
              size="small"
              label={webhook ? 'Save changes' : 'Add endpoint'}
              disabled={submitting || (!!webhook && !form.formState.isDirty)}
            />
          </div>
        </form>
      </FormProvider>
    </Dialog>
  );
};
